import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Condition, EntityParameter, FIELD_SEPARATOR, NodeType, QueryBuilderConfig, QueryNode } from '@ispm-client/modules/query-builder/models/query-builder-config';
import { StringUtils } from '@ispm/shared/utils/String.utils';

@Component({
  selector: 'query-builder',
  templateUrl: './query-builder.component.html',
  styleUrls: ['./query-builder.component.scss'],
})
export class QueryBuilderComponent implements OnInit {

  @Input()
  query: QueryNode;
  @Input()
  config: QueryBuilderConfig;
  @Input()
  currentLevel = 0;
  @Output()
  validateChange: EventEmitter<boolean> = new EventEmitter();

  parameters: EntityParameter[];

  readonly HEAD_NODE = NodeType.HEAD;
  readonly PARAMETER_NODE = NodeType.PARAMETER;
  readonly PARAMETER_SET_NODE = NodeType.PARAMETER_SET;

  ngOnInit(): void {
    this.parameters = Object.keys(this.config.queriables)
      .map(queriable => ({
        name: this.config.queriables[queriable].label,
        key: queriable
      }))
      .sort((paramA, paramB) => paramA.name.toLocaleLowerCase() < paramB.name.toLocaleLowerCase() ? -1 : 1);
    if (this.config.isConditionMandatory !== undefined && this.query.type !== NodeType.PARAMETER_SET){
      this.validateChange.emit(!this.config.isConditionMandatory);
    }
  }

  toggleNegation(rule) {
    rule.negate = !rule.negate;
    this.validateChange.emit();
  }

  logicalOperatorChanged(evt) {
    this.query.condition = evt;
    this.validateChange.emit();
  }

  onParameterRulesChanged(rule, form) {
    rule.rules = form.rules.map(ruleField =>
      Object.assign({ }, ruleField, { fullName: `${StringUtils.cleanNonNumericNonLetters(rule.parameter)}${FIELD_SEPARATOR}${StringUtils.cleanNonNumericNonLetters(ruleField.field)}`}));
    this.validateChange.emit();
  }

  addParameter(query: QueryNode) {
    const newRule = {
      condition: Condition.AND,
      type: NodeType.PARAMETER,
      rules: []
    };
    Object.defineProperty(newRule, 'collapsed', {enumerable: false, value: false, writable: true, configurable: true});
    query.rules.push(newRule);
    this.updateValidity(newRule);
  }

  canAddParameter(query: QueryNode) {
    return !query.rules.some(rule => rule.type === NodeType.PARAMETER_SET);
  }

  addParameterSet(query: QueryNode) {
    const alreadyHasAParamSet = this.query.rules.some(rule => rule.type === NodeType.PARAMETER_SET);
    if (!alreadyHasAParamSet) {
      const newRuleSet = {
        type: NodeType.PARAMETER_SET,
        condition: this.query.condition,
        rules: this.query.rules
      };
      query.rules = [newRuleSet];
    }
    const ruleSet: QueryNode = {
      negate: false,
      condition: Condition.AND,
      type: NodeType.PARAMETER_SET,
      rules: []
    };
    this.addParameter(ruleSet);
    query.rules.push(ruleSet);
    this.updateValidity(ruleSet);
  }

  updateValidity(rule, valid?: boolean) {
    if(rule && (valid !== undefined || rule._valid === undefined)){
      Object.defineProperty(rule, '_valid', {enumerable: false, writable: true, value: valid, configurable: true});
    }
    this.validateChange.emit(this.query.rules.every(_rule => _rule._valid));
  }

  deleteRule(rule: QueryNode) {
    const index = this.query.rules.indexOf(rule);
    this.query.rules.splice(index, 1);
    if (this.query.rules.length === 1 && this.query.rules[0].type === NodeType.PARAMETER_SET) {
      this.query.condition = this.query.rules[0].condition;
      this.query.rules = this.query.rules[0].rules;
    }
    //valid if all rules are valid or if condition is not mandatory and there is only one rule
    this.validateChange.emit((this.query.rules.every(_rule => _rule._valid)) ||
      (this.config.isConditionMandatory === false && this.query.rules.length <= 1 && this.query.type !== NodeType.PARAMETER_SET));
  }

  expandCollapsePanel($event, panel, rule) {
    panel.collapsed = !panel.collapsed;
    rule['collapsed'] = panel.collapsed;
  }

  selectRule(rule){
    rule.rules = [];

    if(rule && !rule.parameter){
      delete rule.parameter;

      if (this.config.isConditionMandatory === undefined){
        rule._valid = false;
      }
      this.validateChange.emit(this.config.isConditionMandatory === false && this.query.rules.length <= 1 && this.query.type !== NodeType.PARAMETER_SET); //undefined is treated as true
    } else {
      this.validateChange.emit();
    }
  }
}
